import React, { useState, useEffect } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import chatService from '@/services/chatService';
import './ChatWindow.css';

const ChatWindow = ({ onClose, isWidget = false }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [agentState, setAgentState] = useState('listening'); // Track agent state

  // Handler to start a new session
  const handleNewSession = async () => {
    console.log('🆕 [ChatWindow] User requested new session');
    setMessages([]); // Clear UI immediately
    setIsLoading(false);
    setIsConnecting(true);
    
    try {
      const result = await chatService.startNewSession();
      if (result.success) {
        console.log('✅ [ChatWindow] New session started successfully');
      }
    } catch (error) {
      console.error('❌ [ChatWindow] Failed to start new session:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Initialize LiveKit connection on mount
  useEffect(() => {
    let unsubscribeMessages = null;
    let unsubscribeSessionReset = null;
    let unsubscribeAgentState = null;
    let isMounted = true;

    const initializeChat = async () => {
      try {
        setIsConnecting(true);
        
        // IMPORTANT: Register message listener BEFORE connecting
        // This ensures we capture messages that arrive during connection (e.g., greeting)
        unsubscribeMessages = chatService.onMessage((message) => {
          console.log('🎯 [ChatWindow] Received message in component:', message);
          console.log('🎯 [ChatWindow] Component mounted:', isMounted);
          if (isMounted) {
            console.log('🎯 [ChatWindow] Updating messages state');
            setMessages(prev => {
              console.log('🎯 [ChatWindow] Previous messages:', prev.length);
              const newMessages = [...prev, message];
              console.log('🎯 [ChatWindow] New messages:', newMessages.length);
              return newMessages;
            });
            // Don't set isLoading to false here - let agent state handle it
          } else {
            console.warn('⚠️ [ChatWindow] Component not mounted, skipping state update');
          }
        });
        console.log('✅ Message listener registered BEFORE connect');
        
        // Listen for session reset events (new LiveKit session)
        unsubscribeSessionReset = chatService.onSessionReset((sessionInfo) => {
          console.log('🆕 [ChatWindow] New session started, clearing messages:', sessionInfo);
          if (isMounted) {
            setMessages([]); // Clear all messages for new session
            setIsLoading(false);
            setAgentState('listening'); // Reset agent state
            console.log('✅ [ChatWindow] Messages cleared for new session');
          }
        });
        
        // Listen for agent state changes
        unsubscribeAgentState = chatService.onAgentStateChange((state) => {
          console.log('🤖 [ChatWindow] Agent state changed to:', state);
          if (isMounted) {
            setAgentState(state);
            // When agent finishes speaking/thinking, stop loading
            if (state === 'listening') {
              setIsLoading(false);
            }
          }
        });
        
        // Initialize and connect to LiveKit
        const result = await chatService.connect();
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;
        
        if (result.success) {
          console.log('✅ Chat service connected');
        } else {
          console.error('❌ Failed to connect to chat service');
          // Show error message
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: 'عذراً، فشل الاتصال بخدمة الدردشة. يرجى المحاولة مرة أخرى.',
            sender: 'system',
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch (error) {
        console.error('❌ Chat initialization error:', error);
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    initializeChat();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
      if (unsubscribeSessionReset) {
        unsubscribeSessionReset();
      }
      if (unsubscribeAgentState) {
        unsubscribeAgentState();
      }
      // IMPORTANT: Don't disconnect here!
      // The connection should persist across component mounts/unmounts
      // Only disconnect when the user explicitly closes the app/tab
      console.log('🔄 ChatWindow unmounting, keeping connection alive');
    };
  }, []); // Empty dependency array - run once on mount

  const handleSendMessage = async (text) => {
    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Send message via LiveKit
      const result = await chatService.sendMessage(text);
      
      if (!result.success) {
        throw new Error('Failed to send message');
      }
      
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Show error message
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'عذراً، فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);
      
      setIsLoading(false);
    }
  };

  return (
    <div className={`chat-window ${isWidget ? 'chat-window-widget' : 'chat-window-app'}`} dir="rtl">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-header-logo">
            <img src="/assets/header-logo.svg" alt="لبيه ميوا" className="header-logo-icon" />
          </div>
        </div>
        <div className="chat-header-actions">
          <button 
            className="chat-new-session-button" 
            onClick={handleNewSession}
            aria-label="بدء محادثة جديدة"
            title="بدء محادثة جديدة"
          >
            <img src="/assets/refresh.svg" alt="تحديث" className="refresh-icon" />
          </button>
          {isWidget && (
            <button 
              className="chat-close-button" 
              onClick={() => {
                console.log('🔴 Close button clicked!');
                console.log('🔴 onClose function:', onClose);
                if (onClose && typeof onClose === 'function') {
                  onClose();
                } else {
                  console.error('❌ onClose is not a function!', onClose);
                }
              }}
              aria-label="إغلاق المحادثة"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      <MessageList 
        messages={messages} 
        isLoading={isLoading} 
        isConnecting={isConnecting}
        agentState={agentState}
        onClickableTextClick={handleSendMessage}
      />
      
      <MessageInput 
        onSendMessage={handleSendMessage} 
        disabled={isLoading || isConnecting}
      />
    </div>
  );
};

export default ChatWindow;
